/**
 * Microsoft Clarity Analytics Utility
 * Safe helpers to instrument events, prioritize session recordings, set tags, and identify users.
 */

/**
 * Dispatch a custom event to Microsoft Clarity.
 * @param {string} eventName - Name of the custom event (e.g. 'exam_started')
 */
export const trackClarityEvent = (eventName) => {
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity("event", eventName);
    }
  } catch (err) {
    console.debug("Clarity event error:", err);
  }
};

/**
 * Upgrade session recording priority in Microsoft Clarity.
 * Used for high-value interactions (such as checkout, exam attempt, submission).
 * @param {string} reason - The upgrade reason (e.g. 'exam_attempt', 'checkout')
 */
export const upgradeClaritySession = (reason) => {
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity("upgrade", reason);
    }
  } catch (err) {
    console.debug("Clarity upgrade error:", err);
  }
};

/**
 * Attach custom tag key-value pairs to the current session in Clarity.
 * @param {string} key - The tag key
 * @param {string|number} value - The tag value
 */
export const setClarityTag = (key, value) => {
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity("set", String(key), String(value));
    }
  } catch (err) {
    console.debug("Clarity set tag error:", err);
  }
};

/**
 * Associate the Clarity session with an authenticated user identifier.
 * @param {string|number} userId - Unique identifier for the user
 * @param {string} [customSessionId] - Optional session ID
 * @param {string} [customPageId] - Optional page ID
 * @param {string} [friendlyName] - Optional user name
 */
export const identifyClarityUser = (userId, customSessionId = null, customPageId = null, friendlyName = null) => {
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function" && userId) {
      window.clarity("identify", String(userId), customSessionId, customPageId, friendlyName);
    }
  } catch (err) {
    console.debug("Clarity identify error:", err);
  }
};

export default {
  trackClarityEvent,
  upgradeClaritySession,
  setClarityTag,
  identifyClarityUser,
};
